import styled from 'styled-components';
import React, { useContext, useMemo } from 'react';
import { TabSelect } from '@/components/tab-select';
import { PriceTestContext } from '@/features/editor/widgets/price-test/context';
import { PTConfigStep } from '@/features/editor/widgets/price-test/props';
import { TinyProgressBar } from '@/components/tiny-progress-bar';
import { PriceTestStepMatchProducts } from '@/features/editor/widgets/price-test/step-match-products';
import { PriceTestStepMatchRule } from '@/features/editor/widgets/price-test/step-match-rule';
import { PriceTestStepQuiz } from '@/features/editor/widgets/price-test/step-quiz';
import { PriceTestStepPages } from '@/features/editor/widgets/price-test/step-pages';
import { PriceTestStepDone } from '@/features/editor/widgets/price-test/step-done';
import { AccountContext } from '@/features/account-context';
import { EditorContext } from '@/features/editor/context/editor-context';
import { Role } from '@/utils/definitions';
import {
  EditorChangeKind,
  PlacementKind,
  TargetingDeviceType,
} from '@/webapi/use-experience-api';
import { useTranspiler } from '@/features/editor/context/use-transpiler';

const TABS = [`MANUAL`, `BY RULE`];

export function PriceTestVariantConfig() {
  const {
    account: {
      store: { role },
    },
  } = useContext(AccountContext);

  const {
    experienceState: { currentExperience, setCurrentVariantIdx },
    inspectorNav: { gotoCompoundEditor },
  } = useContext(EditorContext);
  const { globalJsId, asSingleValueBlock } = useTranspiler();

  const {
    variantIdx,
    props,
    setProps,
    step,
    onNextClicked,
    onBackClicked,
    getProgress,
    getNextText,
    getNextHint,
    navToMatchStep,
  } = useContext(PriceTestContext);

  const shouldShowEditJsButton = useMemo(
    () => role === Role.SYSTEM && !!currentExperience?.priceTestJsPerVariant,
    [role, currentExperience?.priceTestJsPerVariant],
  );

  const matchType = useMemo(
    () => (props?.matchType === `manual` ? TABS[0] : TABS[1]),
    [props?.matchType],
  );
  const onMatchTypeChanged = (idx: number) => {
    setProps((draft) => {
      draft.matchType = idx === 0 ? `manual` : `rule`;
      navToMatchStep();
    });
  };

  const stepClass = useMemo(() => {
    if (step === PTConfigStep.MATCH_STEP) return `first-step`;
    if (step === PTConfigStep.PAGES_STEP) return `last-step`;
    if (step === PTConfigStep.DONE_STEP) return `done`;
    return ``;
  }, [step]);

  const onEditJavascript = () => {
    const variant = currentExperience?.variants[variantIdx];
    const editorId = globalJsId(currentExperience.id);
    let jsChange = variant.changes.find(
      (change) => change.editorId === editorId,
    );

    if (!jsChange) {
      jsChange = asSingleValueBlock(
        EditorChangeKind.GLOBAL_JS,
        editorId,
        `appendJs`,
        ``,
        `div`,
      );
      jsChange.targetDevice = TargetingDeviceType.ALL_DEVICES;
      jsChange.targetPlacement = { kind: PlacementKind.ALL_PAGES };
    }
    setCurrentVariantIdx(variantIdx);
    gotoCompoundEditor(jsChange, false, false);
  };

  return (
    <Wrapper>
      <Header>
        <TabSelect
          tabs={TABS}
          tabHeight={2.8}
          containerPadding={0}
          tabWidth={12}
          tabFont="Inter"
          defaultValue={matchType}
          onTabSelected={onMatchTypeChanged}
        />
      </Header>
      <Content>
        {step === PTConfigStep.MATCH_STEP && (
          <>
            {props.matchType === `manual` && <PriceTestStepMatchProducts />}
            {props.matchType === `rule` && <PriceTestStepMatchRule />}
          </>
        )}
        {step === PTConfigStep.QUIZ_STEP && <PriceTestStepQuiz />}
        {step === PTConfigStep.PAGES_STEP && <PriceTestStepPages />}
        {step === PTConfigStep.DONE_STEP && <PriceTestStepDone />}
      </Content>
      <Footer>
        <TinyProgressBar
          initialValue={getProgress()}
          widthInPercent={100}
          heightInRem={0.5}
          backgroundColor="rgb(240, 244, 248)"
          progressColor="rgb(155, 199, 250)"
        />
        <div className={`buttons-layout ${stepClass}`}>
          {step !== PTConfigStep.MATCH_STEP && (
            <button
              type="button"
              className="back-button"
              onClick={() => onBackClicked()}
            >
              &lt;
            </button>
          )}
          <button
            type="button"
            className="next-button"
            disabled={step === PTConfigStep.DONE_STEP}
            onClick={onNextClicked}
          >
            {getNextText()}
          </button>
          {shouldShowEditJsButton && (
            <button
              type="button"
              className="next-button"
              onClick={onEditJavascript}
            >
              Edit Javascript
            </button>
          )}
        </div>
        <span className="next-hint">{getNextHint()}</span>
      </Footer>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  width: 100%;
  margin-top: 1rem;
  border-radius: 1rem;
  overflow: hidden;

  font-family: Inter, sans-serif;

  && > * {
    padding: 1rem;
  }
`;

const Header = styled.div`
  background-color: white;
  display: flex;
  flex-direction: column;
  width: 100%;

  .tab-select-container > * {
    font-weight: 500 !important;
    color: rgb(136, 148, 160) !important;
  }
`;

const Content = styled.div`
  background-color: rgb(248, 250, 252);
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 30rem;
  padding-top: 0;
  padding-bottom: 0;
`;

const Footer = styled.div`
  background-color: white;
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: center;
  align-items: center;
  padding: 1rem 2rem !important;
  gap: 1.5rem;

  .buttons-layout {
    display: grid;
    grid-template-columns: 1fr 8fr;
    gap: 1rem;
    width: 100%;
  }

  .buttons-layout.last-step {
    .next-button {
      background-color: rgb(61, 71, 245);
      border-color: rgb(61, 71, 245);
      color: white;
    }
  }

  .buttons-layout.done {
    .next-button {
      opacity: 0.2;
      pointer-events: none;
      cursor: not-allowed;
    }
  }

  .buttons-layout.first-step {
    grid-template-columns: 1fr;
  }

  button {
    background-color: white;
    color: black;
    font-size: 1.4rem;
    font-weight: 400;
    border-radius: 0.4rem;
    padding: 1.5rem 2rem;
    width: 100%;
    border: 1px solid rgb(203, 203, 203);
    cursor: pointer;
  }

  .next-hint {
    font-size: 1.2rem;
    color: rgb(182, 186, 193);
    text-align: center;
  }

  .next-hint:empty {
    display: none;
  }

  && > *:last-child {
    padding-bottom: 0.5rem;
  }
`;
